import type { SignupErrors } from '../../types/state'
import {
    COOKIE_AGE_VERIFICATION,
    COOKIE_CONSENT,
    COOKIE_PASSCODE,
    COOKIE_USERNAME,
    COOKIE_USER_ID,
    COOKIE_USER_LEVEL,
} from '../constants'
import {
    API_CHANGE_PASSWORD,
    API_LOGIN,
    API_SIGNUP,
    API_SIGNUP_RANDOM_USERNAME,
    API_SIGNUP_validate,
} from '../constants/endpoints'
import { notifyWorkerOfBrowserReload } from '../state/state.app'
import { controller } from '../utilities/abortController'
import { eraseCookie, getCookie, setCookie } from '../utilities/cookies'
import { utcOffset } from '../utilities/dates'
import { clearSessionStorage } from '../utilities/storage'
import { EdgeApiResponse, fetchStatusResolver } from './api/utilities'

export const getUsername = () => getCookie(COOKIE_USERNAME)
export const getPasscode = () => getCookie(COOKIE_PASSCODE)

/**
 * True for a logged-in account that has not completed verification. The
 * visitor API returns no user for it, the same as for a guest, so `level` is 0
 * in both cases. The full login cookie pair is the difference: an unverified
 * account keeps both passcode and username. Do not use user_id or user_lv
 * here: getVisitorInformation writes them as 0 for every guest.
 * m.myfreecams.com also requires username for this check.
 */
export const isUnverifiedAccount = (user: { level: number }) =>
    user.level === 0 && authenticated()

export const authenticated = () =>
    Boolean(getPasscode()) && Boolean(getUsername())

export const getAgeVerificationCookie = () => getCookie(COOKIE_AGE_VERIFICATION)

export const setAgeVerificationCookie = () => {
    if (typeof window === 'undefined') return
    setCookie(COOKIE_AGE_VERIFICATION, '1')
}

export const setConsentCookie = (refuse: boolean) => {
    if (typeof window === 'undefined') return
    setCookie(COOKIE_CONSENT, refuse ? 'refused' : 'accepted')
}

export const UNVERIFIED_ACCOUNT_IFRAME = `https://www.myfreecams.com/php/purchase.php?request=tokens&style_override=profiles&style=mobile&client=1101`

export interface LoginResponse {
    err: 0 | 1
    errmsg?: string
    id: string
    method: 'login'
    responseVer: 1
    result: null
}

export const login = async (username: string, password: string) => {
    if (typeof window === 'undefined') return
    const response = await fetch(API_LOGIN, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            username,
            password,
            tz: utcOffset,
            ss: `${screen.width}x${screen.height}`,
        }),
        signal: controller.signal,
    })
    return (await response.json()) as LoginResponse
}

export const logout = () => {
    if (typeof window === 'undefined') return
    controller.abort()
    clearSessionStorage()
    eraseCookie(COOKIE_PASSCODE)
    eraseCookie(COOKIE_USER_ID)
    eraseCookie(COOKIE_USER_LEVEL)
    notifyWorkerOfBrowserReload()
    document.location.href = document.location.origin
    document.location.reload()
}

export interface SignupPostFields {
    consent: boolean
    username: string
    password: string
    email: string
}

export type SignupSSOBypass = {
    sso_bypass: boolean
    code: string
}

export type SignupResultTypes =
    'verification_email_sent' | SignupSSOBypass | null

export interface SignupResponse {
    err: number
    errmsg?: SignupErrors
    id: string
    method: 'signup'
    responseVer: number
    result: SignupResultTypes
}

export async function signupPost(fields: SignupPostFields) {
    if (typeof window === 'undefined') return
    const { consent, email, password, username } = fields
    const tz = utcOffset
    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)
    formData.append('confirm_password', password)
    formData.append('email', email)
    formData.append('terms', String(consent))
    formData.append('client_id', '1101')
    formData.append('mode', 'app')
    formData.append('tz', String(tz))
    try {
        const resp = await fetch(API_SIGNUP, {
            method: 'POST',
            credentials: 'include',
            body: formData,
            signal: controller.signal,
        })
        return (await resp.json()) as SignupResponse
    } catch (_ex) {
        const unexpectedErrResponse: SignupResponse = {
            err: 1,
            id: '',
            method: 'signup',
            responseVer: 0,
            result: null,
            errmsg: {
                general: [
                    'An unexpected error has occured. If this error persists, please contact support.',
                ],
            },
        }
        return unexpectedErrResponse
    }
}

export type SignupFields = 'email' | 'username' | 'new_password'

export interface ValidateSignupFields {
    email?: string
    username?: string
    current_password?: string
    new_password?: string
}

export interface ValidateSignupResponse {
    err: number
    errmsg?: {
        username?: Array<string>
        password?: Array<string>
    }
    id: string
    method: 'account/qualifyUserPass'
    responseVer: number
    result: true | null
}

export async function validateSignupPost(
    field: SignupFields,
    fields: ValidateSignupFields,
) {
    const { new_password, username } = fields
    if (field === 'email' || !fields[field]) return
    const password = field === 'new_password'
    const formData = new FormData()
    if (username) formData.append('new_username', username)
    if (password) {
        formData.append('new_password', new_password)
        formData.append('new_password_confirm', new_password)
    }
    const resp = await fetch(API_SIGNUP_validate, {
        method: 'POST',
        credentials: 'include',
        body: formData,
    })
    return (await resp.json()) as ValidateSignupResponse
}

export async function changeUsernamePost(token: string, username: string) {
    const url = 'https://www.myfreecams.com/php/account.php?request=status'
    const formData = new FormData()
    formData.append('new_username', username)
    formData.append('change_username', '1')
    formData.append('token', token)
    return fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: controller.signal,
    }).then(fetchStatusResolver)
}

interface IChangePasswordResponse {
    id?: string
    responseVer?: string
    method?: string
    result?: string // either null or "success"
    err?: number // either 0 (ok) or no problems
    errmsg?: {
        current_password?: string
        desired_password?: string
        errors?: Array<string>
    }
}

export const changePasswordResolver = async (
    response: Response,
    successHandler,
    errorHandler,
) => {
    if (response.status === 200) {
        successHandler([
            'Password changed successfully! Your session will be reloaded shortly.',
        ])
    } else {
        const res: IChangePasswordResponse = await response.json()
        errorHandler(
            res?.errmsg?.errors || [
                'An unknown error occurred. Please try again later or contact support if you see this message repeatedly.',
            ],
        )
    }
}

export async function changePasswordPost(
    token: string,
    currentPassword: string,
    newPassword: string,
) {
    const url = API_CHANGE_PASSWORD
    const formData = new FormData()
    formData.append('current_password', currentPassword)
    formData.append('desired_password', newPassword)
    formData.append('token', token)
    return fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: controller.signal,
    })
}

export interface RandomUsernameResponse extends EdgeApiResponse {
    result: {
        username: string
    }
}

export async function fetchRandomUsername() {
    try {
        const resp = await fetch(API_SIGNUP_RANDOM_USERNAME, {
            credentials: 'include',
            method: 'GET',
            signal: controller.signal,
        })
        const data = (await resp.json()) as RandomUsernameResponse
        const { err, result } = data
        if (err !== 0) throw new Error(`[fetchRandomUsername] Error: ${err}`)
        return result
    } catch (ex) {
        console.error(`[fetchRandomUsername]: Error Fetching ${ex}`)
    }
}

/**
 * Automatically redirect known users that signup
 * @param result
 */
export function handleKnownSignup(result: SignupSSOBypass) {
    document.cookie =
        'sso_signup=true; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/'
    const params = new URLSearchParams({
        code: result.code,
        load: 'app',
        client: 'app',
    })
    const redirectUrl = `https://www.myfreecams.com/?${params.toString()}`
    window.location.href = redirectUrl
}
